<template>
  <div class="contents">
    <div class="layout">
      <UserListHeader title="전시 크롤링" menu="전시 크롤링" />

      <!-- 연결 상태 · 동기화 -->
      <div class="white-panel mt20">
        <div class="crawl-bar">
          <div class="crawl-bar__status">
            <span v-if="!status.configured" class="badge badge--warn">수집 서비스 주소 미설정</span>
            <span v-else-if="!status.reachable" class="badge badge--warn">연결 불가</span>
            <span v-else class="badge badge--ok">연결됨</span>

            <span v-if="status.configured && !status.hasToken" class="badge badge--muted">
              토큰 없음 (일부 출처 제외됨)
            </span>

            <span v-if="status.lastRunStartedAt" class="crawl-bar__last">
              마지막 동기화 {{ formatDateTime(status.lastRunStartedAt) }}
              · 신규 {{ status.lastRunItemsNew ?? 0 }}
              · 변경 {{ status.lastRunItemsUpdated ?? 0 }}
              <em v-if="status.lastRunStatus === 'FAILED'" class="crawl-bar__err">
                (실패: {{ status.lastRunError }})
              </em>
            </span>
            <span v-else class="crawl-bar__last">동기화 이력이 없습니다.</span>
          </div>

          <button
            type="button"
            class="btn btn--primary"
            :disabled="syncing || !status.configured"
            @click="runSync"
          >
            {{ syncing ? '동기화 중…' : '동기화' }}
          </button>
        </div>

        <p v-if="!status.configured" class="crawl-hint">
          Artinerary 공개 주소가 아직 연결되지 않았습니다. 서버 설정
          <code>artinerary.api-url</code> 이 들어오면 동기화할 수 있습니다.
        </p>
      </div>

      <!-- 필터 -->
      <div class="white-panel mt20">
        <div class="crawl-filters">
          <select v-model="filter.status" @change="reload">
            <option value="">전체 상태</option>
            <option value="NEW">미확인</option>
            <option value="REVIEWED">확인함</option>
            <option value="IGNORED">무시</option>
          </select>

          <select v-model="filter.source" @change="reload">
            <option value="">전체 출처</option>
            <option v-for="s in sources" :key="s" :value="s">{{ s }}</option>
          </select>

          <label class="crawl-filters__check">
            <input type="checkbox" v-model="filter.onlyUnregistered" @change="reloadFromFirst" />
            미등록만
          </label>
        </div>

        <!-- 기간 (mimio#77). 시작일·종료일 범위를 따로 받고, 진행 상태 빠른 선택은 오늘 기준으로 서버가 푼다.
             둘을 같이 쓰면 교집합이다. 기간이 비어 있는 후보는 기간 조건을 걸면 빠진다. -->
        <div class="crawl-filters crawl-filters--period">
          <select v-model="filter.phase" @change="reloadFromFirst">
            <option value="">전체 기간</option>
            <option value="ONGOING">진행 중</option>
            <option value="UPCOMING">예정</option>
            <option value="ENDED">종료</option>
          </select>
          <span class="crawl-filters__label">시작일</span>
          <input v-model="filter.startFrom" type="date" @change="reloadFromFirst" />
          <span class="crawl-muted">~</span>
          <input v-model="filter.startTo" type="date" @change="reloadFromFirst" />
          <span class="crawl-filters__label">종료일</span>
          <input v-model="filter.endFrom" type="date" @change="reloadFromFirst" />
          <span class="crawl-muted">~</span>
          <input v-model="filter.endTo" type="date" @change="reloadFromFirst" />
          <button v-if="hasPeriodFilter" type="button" class="btn btn--sm btn--outline" @click="clearPeriod">
            기간 초기화
          </button>
        </div>

        <DataTable
          :columns="columns"
          :rows="rows"
          row-key="id"
          :row-class="rowClass"
          searchable
          search-placeholder="제목 · 장소 검색"
          paged
          :page="page"
          :size="size"
          :total-elements="totalElements"
          :total-pages="totalPages"
          @search="onSearch"
          @update:page="onPage"
          @update:size="onSize"
        >
          <template #cell-no="{ index }">{{ page * size + index + 1 }}</template>

          <!-- 이미지도 상세를 여는 손잡이다. 제목만 눌리면 클릭 과녁이 쓸데없이 작다. -->
          <template #cell-thumbnailUrl="{ row }">
            <button type="button" class="crawl-thumb-btn" @click="openDetail(row.id)">
              <img v-if="row.thumbnailUrl" :src="row.thumbnailUrl" class="crawl-thumb" alt="" />
              <span v-else class="crawl-thumb crawl-thumb--empty">없음</span>
            </button>
          </template>

          <!-- 제목과 장소를 한 칸에 두 줄로 둔다. 표 셀은 기본이 nowrap 이라 컬럼이 늘어날수록
               가로 스크롤이 생기는데, 세로로 쌓으면 그만큼 폭이 준다. -->
          <template #cell-title="{ row }">
            <div class="crawl-cell">
              <div class="crawl-cell__line">
                <!-- 배지 둘을 나눠 쓴다.
                     NEW  : 이번 동기화에서 처음 들어온 것 (다음 동기화가 돌면 사라진다)
                     미확인: 아직 상세를 안 열어본 것 (동기화를 몇 번 돌려도 남는다) -->
                <span v-if="row.firstSeenInLatestRun" class="badge badge--new">NEW</span>
                <span v-if="row.unread" class="badge badge--unread">미확인</span>
                <span v-if="row.registered" class="badge badge--ok">등록됨 · {{ categoryLabel(row.registeredCategory) }}</span>
                <span v-if="row.changedSinceRegistered" class="badge badge--warn">원본 변경</span>
                <span v-if="row.status === 'IGNORED'" class="badge badge--muted">무시</span>
                <a href="#" class="crawl-cell__title" @click.prevent="openDetail(row.id)">
                  {{ row.title || '(제목 없음)' }}
                </a>
              </div>
              <div class="crawl-cell__sub">{{ row.venueName || '장소 미상' }}</div>
            </div>
          </template>

          <template #cell-period="{ row }">
            <div v-if="row.periodFrom" class="crawl-cell crawl-cell--period">
              <div>{{ row.periodFrom }} ~</div>
              <div>{{ row.periodTo || '미정' }}</div>
            </div>
            <span v-else class="crawl-muted">{{ row.periodRaw || '기간 미상' }}</span>
          </template>

          <template #cell-collectedAt="{ row }">
            <div class="crawl-cell crawl-cell--period">
              <div>{{ formatDate(row.collectedAt) }}</div>
              <div class="crawl-cell__sub">{{ formatTime(row.collectedAt) }}</div>
            </div>
          </template>

          <template #cell-action="{ row }">
            <button v-if="!row.registered" type="button" class="btn btn--sm" @click="openDetail(row.id)">
              상세 · 등록
            </button>
            <router-link
              v-else
              class="btn btn--sm btn--outline"
              :to="registeredRoute(row)"
            >
              {{ categoryLabel(row.registeredCategory) }} 보기
            </router-link>
          </template>
        </DataTable>
      </div>
    </div>

    <!-- 상세 · 등록 -->
    <div v-if="detail" class="crawl-modal" @click.self="closeDetail">
      <div class="crawl-modal__box">
        <!-- 전역 common.css 가 header{position:fixed;top:0} 이라 <header> 를 쓰면 제목줄이
             팝업 밖 화면 최상단으로 날아간다. div 로 둔다. -->
        <div class="crawl-modal__head">
          <h3>{{ detail.title || '(제목 없음)' }}</h3>
          <button type="button" class="crawl-modal__x" aria-label="닫기" @click="closeDetail">✕</button>
        </div>

        <div class="crawl-modal__body">
          <form class="crawl-form" @submit.prevent="submitRegister">
            <!-- 위쪽은 2컬럼(이미지 | 짧은 정보), 아래쪽은 1컬럼(본문 같은 긴 정보).
                 이미지는 폭만 고정하고 높이는 auto — object-fit 으로 자르면 비율이 찌그러져
                 포스터의 글자가 잘린다. -->
            <div class="crawl-grid">
              <div class="crawl-grid__media">
                <div class="crawl-preview">
                  <img v-if="previewSrc" :src="previewSrc" alt="" />
                  <div v-else class="crawl-preview__empty">이미지 없음</div>
                </div>

                <div v-if="detail.images?.length > 1" class="crawl-images">
                  <img
                    v-for="img in detail.images"
                    :key="img"
                    :src="img"
                    :class="{ 'is-picked': form.imageUrl === img }"
                    @click="form.imageUrl = img"
                    alt=""
                  />
                </div>

              </div>

              <div class="crawl-grid__fields">
                <p class="crawl-meta">
                  <b>{{ detail.sourceName || detail.sourceSlug }}</b>
                  · 수집 {{ formatDateTime(detail.collectedAt) }}
                  <a v-if="detail.officialUrl" :href="detail.officialUrl" target="_blank" rel="noopener noreferrer">공식 페이지</a>
                  <a v-if="detail.sourceUrl" :href="detail.sourceUrl" target="_blank" rel="noopener noreferrer">수집 원문</a>
                </p>

                <div v-if="detail.registered" class="crawl-note crawl-note--ok">
                  이미 {{ categoryLabel(detail.registeredCategory) }}(으)로 등록된 항목입니다.
                  <router-link :to="registeredRoute(detail)">{{ categoryLabel(detail.registeredCategory) }} 관리에서 보기</router-link>
                  <span v-if="detail.changedSinceRegistered">
                    — 등록 이후 원본이 바뀌었습니다. 반영 여부는 관리 화면에서 직접 판단해주세요.
                  </span>
                </div>

                <!-- 본문 없는 소스가 있다. 결함이 아니라 정책이라 경고가 아니라 안내로 보여준다. -->
                <div v-if="!detail.description" class="crawl-note">
                  이 출처는 원문 본문을 제공하지 않습니다. 아래 <b>본문</b>을 공식 페이지를 보고 직접 입력해야 등록됩니다.
                </div>

                <!-- 어느 원장으로 보낼지 (mimio#77). 비엔날레·아트페어는 이벤트로 가야 한다는 요청이라
                     제목으로 제안만 하고 고르는 건 담당자다. 자동으로 바꾸면 잘못 들어가도 아무도 모른다. -->
                <div v-if="!detail.registered" class="crawl-field">
                  <span class="crawl-field__label">등록할 곳 <span class="crawl-req">필수</span></span>
                  <div class="crawl-category">
                    <label v-for="c in CATEGORIES" :key="c.value" class="crawl-category__item">
                      <input v-model="form.category" type="radio" :value="c.value" />
                      {{ c.label }}
                    </label>
                    <select v-if="form.category === 'NEWS'" v-model="form.newsType" class="crawl-category__news">
                      <option value="">뉴스 분류 선택</option>
                      <option value="COMPETITION">공모</option>
                      <option value="ETC">기타</option>
                    </select>
                  </div>
                  <p v-if="suggestEvent" class="crawl-muted crawl-field__hint">
                    제목에 비엔날레·아트페어가 있습니다. 이벤트로 등록하는 게 맞을 수 있어요.
                    <a href="#" @click.prevent="form.category = 'EVENT'">이벤트로 바꾸기</a>
                  </p>
                </div>

                <label class="crawl-field">
                  <span class="crawl-field__label">제목 <span class="crawl-req">필수</span></span>
                  <input v-model="form.title" type="text" />
                </label>

                <div class="crawl-field__row">
                  <label class="crawl-field">
                    <span class="crawl-field__label">시작일 <span class="crawl-req">필수</span></span>
                    <input v-model="form.periodFrom" type="date" />
                  </label>
                  <label class="crawl-field">
                    <span class="crawl-field__label">종료일 <span class="crawl-req">필수</span></span>
                    <input v-model="form.periodTo" type="date" />
                  </label>
                </div>
                <p v-if="detail.periodRaw" class="crawl-muted">원문 기간 표기: {{ detail.periodRaw }}</p>

                <label class="crawl-field"><span class="crawl-field__label">장소</span><input v-model="form.location" type="text" /></label>
                <label class="crawl-field"><span class="crawl-field__label">주소</span><input v-model="form.locationDetail" type="text" /></label>

                <label class="crawl-field crawl-field--check">
                  <input type="checkbox" v-model="form.isHidden" />
                  숨김으로 등록 (권장 — 검토 후 {{ categoryLabel(form.category) }} 관리에서 공개)
                </label>
              </div>
            </div>

            <!-- 긴 정보는 폭을 다 쓴다 -->
            <div class="crawl-single">
              <label class="crawl-field">
                <span class="crawl-field__label">본문 <span class="crawl-req">필수</span></span>
                <textarea v-model="form.content" rows="8"></textarea>
              </label>

              <div class="crawl-field">
                <span class="crawl-field__label">원문 링크</span>
                <div class="crawl-field__with-action">
                  <input v-model="form.originLink" type="text" />
                  <!-- 값이 입력칸에 있는 채로 원문을 바로 확인할 수 있어야 한다. 담당자가 URL 을
                       복사해 주소창에 붙이는 동작을 없앤다. -->
                  <a
                    v-if="safeOriginLink"
                    class="btn btn--outline btn--sm"
                    :href="safeOriginLink"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    새 탭으로 열기 ↗
                  </a>
                  <span v-else-if="form.originLink" class="crawl-muted crawl-field__hint">열 수 있는 주소가 아닙니다</span>
                </div>
              </div>

              <label class="crawl-field">
                <span class="crawl-field__label">대표 이미지 URL <span class="crawl-req">필수</span></span>
                <input v-model="form.imageUrl" type="text" :disabled="!!form.imageFile" />
              </label>
              <!-- 수집 이미지를 못 받을 때의 대안 (mimio#77). 파일을 고르면 URL 대신 이 파일로 등록한다.
                   서버가 원격 이미지와 같은 기준(15MB · 실제 바이트로 JPEG·PNG·GIF·WebP 판정)으로 검사한다. -->
              <div class="crawl-field">
                <span class="crawl-field__label">또는 이미지 파일 직접 올리기</span>
                <div class="crawl-field__with-action">
                  <input
                    ref="imageFileInput"
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    @change="onPickImageFile"
                  />
                  <button v-if="form.imageFile" type="button" class="btn btn--sm btn--outline" @click="clearImageFile">
                    파일 취소
                  </button>
                </div>
                <span v-if="form.imageFile" class="crawl-muted crawl-field__hint">
                  {{ form.imageFile.name }} ({{ Math.round(form.imageFile.size / 1024) }}KB) — 이 파일로 등록합니다
                </span>
              </div>
              <p class="crawl-muted">
                등록하면 이 이미지를 내려받아 우리 저장소로 복사합니다. 원본이 지워져도 등록한 콘텐츠는 유지됩니다.
                <b>미리보기가 보인다고 등록이 되는 건 아닙니다</b> — 미리보기는 브라우저가 출처 사이트에서
                직접 받아온 것이라, 출처가 서버 주소를 막아둔 곳이면 등록 단계에서 실패할 수 있습니다.
              </p>

              <details v-if="hasExtras" class="crawl-extras">
                <summary>기타 정보</summary>
                <dl>
                  <template v-for="(v, k) in detail.sourceExtras" :key="k">
                    <dt>{{ k }}</dt>
                    <dd>{{ v }}</dd>
                  </template>
                  <template v-if="detail.openingHours"><dt>관람시간</dt><dd>{{ detail.openingHours }}</dd></template>
                  <template v-if="detail.admissionFee"><dt>관람료</dt><dd>{{ detail.admissionFee }}</dd></template>
                  <template v-if="detail.contact"><dt>문의</dt><dd>{{ detail.contact }}</dd></template>
                  <template v-if="detail.artists?.length"><dt>작가</dt><dd>{{ detail.artists.join(', ') }}</dd></template>
                </dl>
              </details>

              <p v-if="missingFields.length" class="crawl-note crawl-note--warn">
                등록하려면 다음이 더 필요합니다 — {{ missingFields.join(' · ') }}
              </p>
            </div>

            <div class="crawl-modal__foot">
              <button type="button" class="btn btn--outline" @click="closeDetail">닫기</button>

              <div class="crawl-modal__foot-right">
                <button
                  v-if="detail.status !== 'IGNORED'"
                  type="button"
                  class="btn btn--outline"
                  @click="setIgnored(true)"
                >
                  무시
                </button>
                <button v-else type="button" class="btn btn--outline" @click="setIgnored(false)">무시 해제</button>

                <button
                  type="submit"
                  class="btn btn--primary"
                  :disabled="detail.registered || registering || missingFields.length > 0"
                >
                  {{ detail.registered ? '이미 등록됨' : registering ? '등록 중…' : '등록하기' }}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import UserListHeader from '../user/UserListHeader.vue';
import DataTable from '@/components/common/DataTable.vue';
import { apiClient } from '@/services/auth-header';

// 제목·장소를 한 칸에 두 줄로 합쳐 컬럼 수를 줄였다 — 가로 스크롤을 없애는 게 목적이다.
const columns = [
  { key: 'no', label: 'No', width: '56px' },
  { key: 'thumbnailUrl', label: '이미지', width: '96px' },
  { key: 'title', label: '제목 · 장소' },
  { key: 'period', label: '기간', width: '118px' },
  { key: 'sourceSlug', label: '출처', width: '92px' },
  { key: 'collectedAt', label: '수집시각', width: '100px' },
  { key: 'action', label: '', width: '104px' },
];

const rows = ref([]);
const sources = ref([]);
const status = ref({ configured: false, hasToken: false, reachable: false });
const filter = ref({
  status: '',
  source: '',
  onlyUnregistered: false,
  phase: '',
  startFrom: '',
  startTo: '',
  endFrom: '',
  endTo: '',
});

// 등록할 원장 (mimio#77). value 는 서버 계약, route 는 ArticleDetail 의 articleType.
const CATEGORIES = [
  { value: 'EXHIBITION', label: '전시', route: 'exhibitions' },
  { value: 'EVENT', label: '이벤트', route: 'events' },
  { value: 'NEWS', label: '뉴스', route: 'news' },
];
const categoryOf = (v) => CATEGORIES.find((c) => c.value === v) ?? CATEGORIES[0];
const categoryLabel = (v) => categoryOf(v).label;
const registeredRoute = (r) => ({
  name: 'ArticleDetail',
  params: { articleType: categoryOf(r.registeredCategory).route, uuid: r.registeredUuid },
});

// 제안만 한다. 고르는 건 담당자다.
const EVENT_HINT = /비엔날레|트리엔날레|아트\s*페어|biennale|triennale|art\s*fair/i;
const suggestEvent = computed(
  () => form.value.category === 'EXHIBITION' && EVENT_HINT.test(form.value.title ?? ''),
);

const hasPeriodFilter = computed(() => {
  const f = filter.value;
  return !!(f.phase || f.startFrom || f.startTo || f.endFrom || f.endTo);
});

const imageFileInput = ref(null);
const imageFilePreview = ref('');
const previewSrc = computed(() => imageFilePreview.value || form.value.imageUrl);
const page = ref(0);
const size = ref(20);
const totalElements = ref(0);
const totalPages = ref(0);
const keyword = ref('');
const syncing = ref(false);
const registering = ref(false);
const detail = ref(null);
const form = ref({});

const hasExtras = computed(
  () => detail.value && detail.value.sourceExtras && Object.keys(detail.value.sourceExtras).length > 0,
);

// 서버(ExhibitionCrawlService.register)의 필수 조건과 같은 목록이다. 눌러보고 나서 실패를 알리면
// 무엇이 없는지 모른 채 다시 열어야 한다 — 미리 보여주고 버튼을 잠근다.
const missingFields = computed(() => {
  if (!detail.value || detail.value.registered) return [];
  const f = form.value;
  const missing = [];
  if (!f.title?.trim()) missing.push('제목');
  if (!f.content?.trim()) missing.push('본문');
  if (!f.imageUrl?.trim() && !f.imageFile) missing.push('대표 이미지');
  if (!f.periodFrom || !f.periodTo) missing.push('기간');
  if (f.category === 'NEWS' && !f.newsType) missing.push('뉴스 분류');
  return missing;
});

// 입력칸의 주소를 그대로 href 에 꽂지 않는다. 담당자가 고칠 수 있는 값이고 원본도 수집 데이터라
// javascript: · data: 같은 스킴이 들어오면 클릭 한 번으로 이 화면에서 스크립트가 돈다.
// http(s) 로 파싱되는 것만 링크로 살린다 — 아니면 버튼 자체를 안 그린다.
const safeOriginLink = computed(() => {
  const raw = (form.value.originLink ?? '').trim();
  if (!raw) return null;
  try {
    const u = new URL(raw);
    return u.protocol === 'http:' || u.protocol === 'https:' ? u.href : null;
  } catch (e) {
    return null;
  }
});

function formatDateTime(v) {
  if (!v) return '-';
  return String(v).replace('T', ' ').slice(0, 16);
}
function formatDate(v) {
  if (!v) return '-';
  return String(v).slice(0, 10);
}
function formatTime(v) {
  if (!v) return '';
  return String(v).replace('T', ' ').slice(11, 16);
}

// 서버가 보낸 사유를 그대로 보여준다. 전역 핸들러는 본문을 문자열로 주고,
// 크롤링 전용 핸들러는 {message} 로 준다 — 둘 다 받는다.
function errorMessage(e, fallback) {
  const data = e?.response?.data;
  if (typeof data === 'string' && data) return data;
  return data?.message ?? fallback;
}

function rowClass(row) {
  if (row.status === 'IGNORED') return 'row-ignored';
  if (row.unread) return 'row-unread';
  return '';
}

async function loadStatus() {
  try {
    status.value = await apiClient.get('/exhibition-crawl/status').then((r) => r.data);
  } catch (e) {
    status.value = { configured: false, hasToken: false, reachable: false };
  }
}

async function loadSources() {
  try {
    sources.value = await apiClient.get('/exhibition-crawl/sources').then((r) => r.data);
  } catch (e) {
    sources.value = [];
  }
}

async function reload() {
  const res = await apiClient.get('/exhibition-crawl', {
    params: {
      status: filter.value.status || undefined,
      source: filter.value.source || undefined,
      q: keyword.value || undefined,
      // 미등록·기간은 서버가 거른다 (mimio#77). 화면에서 거르면 쪽마다 개수가 들쭉날쭉하고 총 건수가 틀린다.
      onlyUnregistered: filter.value.onlyUnregistered || undefined,
      phase: filter.value.phase || undefined,
      startFrom: filter.value.startFrom || undefined,
      startTo: filter.value.startTo || undefined,
      endFrom: filter.value.endFrom || undefined,
      endTo: filter.value.endTo || undefined,
      page: page.value,
      size: size.value,
    },
  });
  rows.value = res.data.content ?? [];
  totalElements.value = res.data.totalElements ?? 0;
  totalPages.value = res.data.totalPages ?? 0;
}

function reloadFromFirst() {
  page.value = 0;
  reload();
}

function clearPeriod() {
  Object.assign(filter.value, { phase: '', startFrom: '', startTo: '', endFrom: '', endTo: '' });
  reloadFromFirst();
}

function onSearch(q) {
  keyword.value = q;
  page.value = 0;
  reload();
}
function onPage(p) {
  page.value = p;
  reload();
}
function onSize(s) {
  size.value = s;
  page.value = 0;
  reload();
}

async function runSync() {
  syncing.value = true;
  try {
    const res = await apiClient.post('/exhibition-crawl/sync').then((r) => r.data);
    alert(
      `동기화 완료 — 신규 ${res.itemsNew}건, 변경 ${res.itemsUpdated}건, 변화 없음 ${res.itemsUnchanged}건` +
        ` (조회 ${res.itemsFetched}건${res.since ? '' : ' · 전체'})`,
    );
    await Promise.all([loadStatus(), loadSources(), reload()]);
  } catch (e) {
    alert(errorMessage(e, '동기화에 실패했습니다.'));
    await loadStatus();
  } finally {
    syncing.value = false;
  }
}

async function openDetail(id) {
  const d = await apiClient.get(`/exhibition-crawl/${encodeURIComponent(id)}`).then((r) => r.data);
  detail.value = d;
  form.value = {
    title: d.title ?? '',
    content: d.description ?? '',
    periodFrom: d.periodFrom ?? '',
    periodTo: d.periodTo ?? '',
    location: d.venueName ?? '',
    locationDetail: d.venueAddress ?? '',
    originLink: d.officialUrl ?? d.sourceUrl ?? '',
    imageUrl: d.images?.[0] ?? '',
    isHidden: true,
    category: 'EXHIBITION',
    newsType: '',
    imageFile: null,
  };
  resetImagePreview();
}

function onPickImageFile(ev) {
  const file = ev.target.files?.[0] ?? null;
  resetImagePreview();
  form.value.imageFile = file;
  if (file) imageFilePreview.value = URL.createObjectURL(file);
}

function clearImageFile() {
  form.value.imageFile = null;
  resetImagePreview();
  if (imageFileInput.value) imageFileInput.value.value = '';
}

function resetImagePreview() {
  if (imageFilePreview.value) URL.revokeObjectURL(imageFilePreview.value);
  imageFilePreview.value = '';
}

function closeDetail() {
  resetImagePreview();
  detail.value = null;
  // 상세를 열면 서버가 "확인함" 으로 바꾸므로 목록을 다시 받아 배지를 맞춘다.
  reload();
}

async function submitRegister() {
  if (missingFields.value.length) {
    alert(`등록하려면 다음이 더 필요합니다 — ${missingFields.value.join(' · ')}`);
    return;
  }
  registering.value = true;
  try {
    const { imageFile, ...fields } = form.value;
    const body = {
      ...fields,
      periodFrom: fields.periodFrom || null,
      periodTo: fields.periodTo || null,
      newsType: fields.category === 'NEWS' ? fields.newsType : null,
    };
    const url = `/exhibition-crawl/${encodeURIComponent(detail.value.id)}/register`;
    if (imageFile) {
      // 파일이 있으면 multipart 로 보낸다. req 파트는 JSON 이어야 서버가 같은 DTO 로 읽는다.
      const fd = new FormData();
      fd.append('req', new Blob([JSON.stringify(body)], { type: 'application/json' }));
      fd.append('image', imageFile);
      // 기본 헤더가 JSON 이라 그대로 두면 axios 가 FormData 를 JSON 으로 바꿔버린다. 다른 업로드 화면과 같게 둔다.
      await apiClient.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    } else {
      await apiClient.post(url, body);
    }
    const label = categoryLabel(fields.category);
    alert(`${label}(으)로 등록했습니다. ${label} 관리에서 확인해주세요.`);
    closeDetail();
  } catch (e) {
    const msg = errorMessage(e, '등록에 실패했습니다.');
    // 이미지 때문에 막혔으면 대안을 같이 알려준다.
    const imageFailed = !form.value.imageFile && /이미지/.test(msg);
    alert(imageFailed ? `${msg}\n\n아래 「이미지 파일 직접 올리기」로 파일을 올려 등록할 수 있습니다.` : msg);
  } finally {
    registering.value = false;
  }
}

async function setIgnored(ignored) {
  await apiClient.post(`/exhibition-crawl/${encodeURIComponent(detail.value.id)}/ignore`, null, {
    params: { ignored },
  });
  closeDetail();
}

onMounted(async () => {
  await Promise.all([loadStatus(), loadSources()]);
  await reload();
});
</script>

<style scoped>
/* 버튼 — 전역 .btn 이 없어서 여기서 정의한다. 값은 DataTable 의 검색 버튼과 맞췄다. */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 34px;
  padding: 0 16px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: #fff;
  color: #333;
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
  white-space: nowrap;
  text-decoration: none;
}
.btn:hover { background: #f5f6f8; }
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.btn--primary { border-color: #2563eb; background: #2563eb; color: #fff; }
.btn--primary:hover { background: #1d4ed8; }
.btn--primary:disabled:hover { background: #2563eb; }
.btn--outline { border-color: #d1d5db; background: #fff; color: #444; }
.btn--sm { height: 28px; padding: 0 10px; font-size: 12px; }

.crawl-bar { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.crawl-bar__status { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.crawl-bar__last { color: #666; font-size: 13px; }
.crawl-bar__err { color: #c0392b; font-style: normal; }
.crawl-hint { margin-top: 10px; color: #8a6d3b; font-size: 13px; }
.crawl-filters { display: flex; gap: 10px; align-items: center; margin-bottom: 12px; }
.crawl-filters__check { display: flex; align-items: center; gap: 4px; font-size: 13px; }
.crawl-filters--period { flex-wrap: wrap; }
.crawl-filters--period input[type='date'] { height: 34px; padding: 0 8px; border: 1px solid #d1d5db; border-radius: 4px; font-size: 13px; }
.crawl-filters__label { font-size: 13px; color: #555; margin-left: 6px; }
.crawl-category { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
.crawl-category__item { display: inline-flex; align-items: center; gap: 4px; font-size: 13px; cursor: pointer; }
.crawl-category__news { height: 30px; font-size: 13px; }
.crawl-thumb-btn { border: 0; background: none; padding: 0; cursor: pointer; display: block; }
.crawl-thumb { width: 76px; height: 54px; object-fit: cover; display: block; }
.crawl-thumb--empty { display: inline-flex; align-items: center; justify-content: center;
  background: #f2f2f2; color: #999; font-size: 12px; }

/* 표 셀은 공통 스타일이 nowrap 이다. 두 줄로 쌓는 칸에서만 풀어준다. */
.crawl-cell { white-space: normal; }
.crawl-cell__line { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.crawl-cell__title { word-break: break-word; }
.crawl-cell__sub { color: #777; font-size: 12px; margin-top: 2px; }
.crawl-cell--period { line-height: 1.45; }
.crawl-muted { color: #888; font-size: 12px; }

.badge { display: inline-block; padding: 1px 6px; border-radius: 3px; font-size: 11px; line-height: 18px; }
.badge--new { background: #e8f0fe; color: #1a56c4; }
.badge--unread { background: #fff3cd; color: #8a6d3b; }
.badge--ok { background: #e6f4ea; color: #1e7e34; }
.badge--warn { background: #fdecea; color: #c0392b; }
.badge--muted { background: #eee; color: #777; }
:deep(.row-unread) { background: #fffdf5; }
:deep(.row-ignored) { opacity: 0.5; }

.crawl-modal { position: fixed; inset: 0; background: rgba(0,0,0,.45); display: flex;
  align-items: center; justify-content: center; z-index: 1000; }
.crawl-modal__box { background: #fff; width: 1100px; max-width: 94vw; max-height: 90vh;
  display: flex; flex-direction: column; border-radius: 6px; }
.crawl-modal__head { display: flex; align-items: center; justify-content: space-between;
  padding: 16px 24px; border-bottom: 1px solid #eee; }
.crawl-modal__head h3 { font-size: 16px; margin: 0; word-break: break-word; }
.crawl-modal__x { border: 0; background: none; font-size: 16px; color: #888; cursor: pointer; padding: 4px 6px; }
.crawl-modal__body { padding: 20px 24px; overflow-y: auto; }

/* 위: 이미지 | 짧은 정보 · 아래: 긴 정보 한 칸 */
.crawl-grid { display: grid; grid-template-columns: 360px minmax(0, 1fr); gap: 24px; align-items: start; }
.crawl-grid__media { display: flex; flex-direction: column; gap: 10px; }
.crawl-grid__fields { display: flex; flex-direction: column; gap: 10px; min-width: 0; }
.crawl-single { display: flex; flex-direction: column; gap: 10px; margin-top: 18px; }

/* 폭만 고정하고 높이는 비율대로 — 포스터가 찌그러지지 않게 object-fit 을 쓰지 않는다. */
.crawl-preview { width: 100%; background: #fafafa; border: 1px solid #eee; border-radius: 4px; }
.crawl-preview img { display: block; width: 100%; height: auto; border-radius: 4px; }
.crawl-preview__empty { display: flex; align-items: center; justify-content: center;
  height: 220px; color: #999; font-size: 13px; }
.crawl-images { display: flex; gap: 6px; flex-wrap: wrap; }
.crawl-images img { width: 76px; height: auto; cursor: pointer; border: 2px solid transparent; border-radius: 3px; }
.crawl-images img.is-picked { border-color: #2563eb; }

.crawl-form { display: flex; flex-direction: column; }
.crawl-field { display: flex; flex-direction: column; gap: 4px; font-size: 13px; }
.crawl-field input[type='text'],
.crawl-field input[type='date'],
.crawl-field textarea {
  border: 1px solid #d1d5db; border-radius: 4px; padding: 7px 10px; font-size: 13px; width: 100%;
}
.crawl-field textarea { resize: vertical; }
.crawl-field__row { display: flex; gap: 10px; }
.crawl-field__with-action { display: flex; gap: 8px; align-items: center; }
.crawl-field__with-action input { flex: 1; min-width: 0; }
.crawl-field__hint { white-space: nowrap; }
.crawl-field__row .crawl-field { flex: 1; }
.crawl-field--check { flex-direction: row; align-items: center; gap: 6px; }
.crawl-field--check input { width: auto; }
.crawl-field__label { display: inline-flex; align-items: center; gap: 6px; }
.crawl-req { color: #c0392b; font-size: 11px; }

.crawl-meta { color: #666; font-size: 13px; display: flex; gap: 10px; flex-wrap: wrap; margin: 0; }
.crawl-note { background: #f7f7f7; padding: 10px 12px; border-radius: 4px; font-size: 13px; }
.crawl-note--ok { background: #e6f4ea; color: #1e7e34; }
.crawl-note--warn { background: #fdecea; color: #c0392b; }
.crawl-extras dt { font-weight: 600; font-size: 12px; margin-top: 6px; }
.crawl-extras dd { margin: 0; font-size: 13px; color: #444; }

/* 닫기는 왼쪽, 무시·등록하기는 오른쪽 — 한 덩어리로 안 보이게 떼어 놓는다. */
.crawl-modal__foot { display: flex; align-items: center; justify-content: space-between;
  gap: 12px; margin-top: 22px; padding-top: 16px; border-top: 1px solid #eee; }
.crawl-modal__foot-right { display: flex; gap: 10px; }

@media (max-width: 960px) {
  .crawl-grid { grid-template-columns: 1fr; }
}
</style>
